/* eslint-disable prettier/prettier */
import { useMemo, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { LocalVideoTrack, Participant, RemoteAudioTrack, RemoteParticipant, RemoteVideoTrack } from 'twilio-video';
import { Box, Tooltip, Typography } from '@mui/material';
import {
  useIsTrackEnabled,
  useIsTrackSwitchedOff,
  useIsGuestCall,
  useMutedParticipants,
  useParticipantIsReconnecting,
  useParticipantNQ,
  usePublications,
  useLocalStorageState,
  useRoomHost,
  useRoomState,
  useSelectedParticipant,
  useTrack,
} from 'hooks';
import { getPreferredFullName, parseParticipantIdentity } from 'utils';
import {
  StarIcon,
  UserIcon,
  MicIcon,
  MicOffIcon,
  NQIcon,
  PinIcon,
  PipIcon,
  VideoCameraIcon,
  VideoCameraOffIcon,
  ZoomInIcon,
  ZoomOutIcon,
} from 'assets';
import { ParticipantDotMenu, ReconnectingAnimation } from 'components';
import { IMutedParticipantsContext } from 'context';
import { ApiUser, ApiWaitingRoomLinkProfile } from 'types';
import { useAppSelector } from 'store';
import { selectUser } from 'store/user';
import { selectWaitingRoomProfile } from 'store/waitingRoom';
import { useTranslation } from 'react-i18next';

import styles from './ParticipantInfo.module.scss';

interface ParticipantInfoProps {
  participant: Participant;
  children: ReactNode;
  hideParticipant?: boolean;
  withIdentity?: boolean;
  withBorder?: boolean;
  isLocalParticipant?: boolean;
  showSelfViewToggle?: boolean;
  dotMenuPosition?: 'top' | 'bottom';
}

export default function ParticipantInfo({
  participant,
  children,
  hideParticipant,
  withIdentity,
  withBorder,
  isLocalParticipant,
  showSelfViewToggle,
  dotMenuPosition = 'top',
}: ParticipantInfoProps) {
  const { t } = useTranslation();
  const isGuestCall = useIsGuestCall();
  const forceLightThemeForGuest = isGuestCall && localStorage.getItem('app-theme') === null;
  const publications = usePublications(participant);
  const { isHost } = useRoomHost();
  const [selectedParticipant] = useSelectedParticipant();
  const { mutedParticipantsId }: IMutedParticipantsContext = useMutedParticipants();

  const [id, identity] = useMemo(() => {
    return parseParticipantIdentity(participant);
  }, [participant]);

  const videoPublication = publications.find((p) => !p.trackName.includes('screen') && p.kind === 'video');
  const isVideoEnabled = Boolean(videoPublication);
  const videoTrack = useTrack(videoPublication) as LocalVideoTrack | RemoteVideoTrack | undefined;
  const isVideoTrackEnabled = useIsTrackEnabled(videoTrack as LocalVideoTrack | RemoteVideoTrack);
  const isVideoSwitchedOff = useIsTrackSwitchedOff(videoTrack as LocalVideoTrack | RemoteVideoTrack);
  const isVideoOff = !isVideoEnabled || !isVideoTrackEnabled || isVideoSwitchedOff;

  const audioPublication = publications.find((p) => p.kind === 'audio');
  const audioTrack = useTrack(audioPublication) as RemoteAudioTrack | undefined;
  const isAudioEnabled = useIsTrackEnabled(audioTrack as RemoteAudioTrack);

  const isParticipantReconnecting = useParticipantIsReconnecting(participant);
  const nq: number = useParticipantNQ(participant);
  const roomState = useRoomState();
  const isReconnecting = roomState === 'reconnecting';

  const user: ApiUser | null = useAppSelector(selectUser);
  const waitingRoomProfile: ApiWaitingRoomLinkProfile | null = useAppSelector(selectWaitingRoomProfile);

  const { clinicUser } = user || {};
  const { preferedDisplayName: clinicUserPreferredDisplayName } = clinicUser || {};
  const { preferedDisplayName: waitingRoomProfilePreferredDisplayName, providerId } = waitingRoomProfile || {};
  const showPreferredFullName = (isHost && id === clinicUser?.id) || (!isHost && id === providerId);

  const participantMuted: boolean = useMemo(() => {
    const isMuted: boolean = mutedParticipantsId.some((mutedParticipantId: string) => mutedParticipantId === id);
    return isMuted;
  }, [id, mutedParticipantsId]);

  const showStar: boolean = useMemo(() => {
    return (isHost && id === user?.clinicUser.id) || (!isHost && id === waitingRoomProfile?.providerId);
  }, [isHost, id, waitingRoomProfile, user]);

  const preferredFullName = useMemo(() => {
    const clinicUserFullName = getPreferredFullName(clinicUserPreferredDisplayName, null);
    const waitingRoomFullName = getPreferredFullName(waitingRoomProfilePreferredDisplayName, identity);

    return showPreferredFullName ? clinicUserFullName ?? waitingRoomFullName : identity;
  }, [isHost, id, waitingRoomProfilePreferredDisplayName, clinicUserPreferredDisplayName]);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const lastTapRef = useRef<number | null>(null);
  const [isSelfViewCollapsed, setIsSelfViewCollapsed] = useLocalStorageState<boolean>(
    'speakerViewSelfViewCollapsed',
    false,
  );
  const [showSelfViewNotice, setShowSelfViewNotice] = useState(false);
  const selfViewNoticeTimerRef = useRef<number | null>(null);
  const previousVideoOffRef = useRef(isVideoOff);
  const isSelfViewToggleEnabled = Boolean(isLocalParticipant && showSelfViewToggle);
  const isSelfViewCompact = isSelfViewToggleEnabled && isSelfViewCollapsed;

  const clearSelfViewNotice = useCallback(() => {
    if (selfViewNoticeTimerRef.current) {
      window.clearTimeout(selfViewNoticeTimerRef.current);
      selfViewNoticeTimerRef.current = null;
    }

    setShowSelfViewNotice(false);
  }, []);

  const showSelfViewNoticeBubble = useCallback(() => {
    clearSelfViewNotice();
    setShowSelfViewNotice(true);
    selfViewNoticeTimerRef.current = window.setTimeout(() => {
      setShowSelfViewNotice(false);
      selfViewNoticeTimerRef.current = null;
    }, 4000);
  }, [clearSelfViewNotice]);

  const toggleFullscreen = useCallback(async () => {
    if (!containerRef.current) {
      return;
    }

    if (document.fullscreenElement) {
      try {
        await document.exitFullscreen();
      } catch {
        // no-op
      }
      return;
    }

    try {
      await containerRef.current.requestFullscreen();
    } catch {
      // Safari may block silently
    }
  }, []);

  const handleMobileTap = () => {
    const now = Date.now();

    if (lastTapRef.current && now - lastTapRef.current < 300) {
      toggleFullscreen();
      lastTapRef.current = null;
    } else {
      lastTapRef.current = now;
    }
  };

  const isMobile = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  }, []);

  const [isPiP, setIsPiP] = useState(false);

  useEffect(() => {
    const handlePiPChange = () => {
      const video = containerRef.current?.querySelector('video') as HTMLVideoElement | null;

      setIsPiP(Boolean(video && document.pictureInPictureElement === video));
    };

    document.addEventListener('enterpictureinpicture', handlePiPChange);
    document.addEventListener('leavepictureinpicture', handlePiPChange);

    return () => {
      document.removeEventListener('enterpictureinpicture', handlePiPChange);
      document.removeEventListener('leavepictureinpicture', handlePiPChange);
    };
  }, []);

  const togglePiP = async () => {
    if (!document.pictureInPictureEnabled) {
      return;
    }

    const video = containerRef.current?.querySelector('video') as HTMLVideoElement | null;

    if (!video) {
      return;
    }

    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      } else {
        await video.requestPictureInPicture();
      }
    } catch {
      // Browser may block or video may not be ready
    }
  };

  const toggleSelfView = useCallback(() => {
    const nextCollapsed = !isSelfViewCollapsed;
    setIsSelfViewCollapsed(nextCollapsed);

    if (nextCollapsed && !isVideoOff) {
      showSelfViewNoticeBubble();
    } else {
      clearSelfViewNotice();
    }
  }, [clearSelfViewNotice, isSelfViewCollapsed, isVideoOff, setIsSelfViewCollapsed, showSelfViewNoticeBubble]);

  useEffect(() => {
    return () => {
      if (selfViewNoticeTimerRef.current) {
        window.clearTimeout(selfViewNoticeTimerRef.current);
        selfViewNoticeTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!isSelfViewToggleEnabled) {
      previousVideoOffRef.current = isVideoOff;
      return;
    }

    if (!isSelfViewCollapsed) {
      clearSelfViewNotice();
      previousVideoOffRef.current = isVideoOff;
      return;
    }

    const cameraTurnedOnWhileHidden = previousVideoOffRef.current && !isVideoOff;
    previousVideoOffRef.current = isVideoOff;

    if (cameraTurnedOnWhileHidden) {
      showSelfViewNoticeBubble();
      return;
    }

    if (isVideoOff) {
      clearSelfViewNotice();
    }
  }, [clearSelfViewNotice, isSelfViewCollapsed, isSelfViewToggleEnabled, isVideoOff, showSelfViewNoticeBubble]);

  const renderSelfViewStatus = (compact = false) => {
    const micMuted = !isAudioEnabled || participantMuted;
    const cameraOff = isVideoOff;

    return (
      <Box className={classnames(styles.selfViewIdentity, { [styles.selfViewIdentityCompact]: compact })}>
        <span className={classnames(styles.selfViewStatusIcon, { [styles.selfViewStatusIconMuted]: micMuted })}>
          {micMuted ? (
            <MicOffIcon sx={{ color: 'var(--app-error)', fontSize: '1rem' }} />
          ) : (
            <MicIcon sx={{ color: 'var(--app-brand-primary)', fontSize: '1rem' }} />
          )}
        </span>
        <span className={classnames(styles.selfViewStatusIcon, { [styles.selfViewStatusIconMuted]: cameraOff })}>
          {cameraOff ? (
            <VideoCameraOffIcon sx={{ color: 'var(--app-error)', fontSize: '1rem' }} />
          ) : (
            <VideoCameraIcon sx={{ color: 'var(--app-brand-primary)', fontSize: '1rem' }} />
          )}
        </span>
        <Typography
          variant='body1'
          component='span'
          className={classnames(styles.selfViewLabel, { [styles.selfViewLabelCompact]: compact })}
        >
          {t('chatYou')}
        </Typography>
      </Box>
    );
  };

  const renderSelfViewToggleButton = (compact = false) => {
    const button = (
      <button
        type='button'
        onPointerDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
          toggleSelfView();
        }}
        className={compact ? styles.selfViewToggleButtonCompact : styles.selfViewToggleButtonExpanded}
        aria-label={compact ? t('showSelfView') : t('hideSelfView')}
      >
        {compact ? <ZoomInIcon /> : <ZoomOutIcon />}
      </button>
    );

    return (
      <Tooltip title={compact ? t('showSelfView') : t('hideSelfView')} placement='right' arrow enterDelay={250}>
        {button}
      </Tooltip>
    );
  };

  return (
    <Box
      ref={containerRef}
      onDoubleClick={!isMobile ? toggleFullscreen : undefined}
      onClick={isMobile ? handleMobileTap : undefined}
      className={classnames(styles.container, {
        [styles.hideParticipant]: hideParticipant,
        [styles.guestLight]: forceLightThemeForGuest,
        [styles.selfViewCompact]: isSelfViewCompact,
      })}
      sx={(muiTheme) => ({
        backgroundColor: isVideoOff
          ? forceLightThemeForGuest
            ? '#F3F3F3'
            : 'var(--app-video-muted)'
          : 'var(--app-page-bg)',
        border: muiTheme.palette.mode === 'dark' || withBorder ? '1px solid var(--app-video-pane-outline)' : 'none',
        boxShadow: muiTheme.palette.mode === 'dark' || withBorder ? 'var(--app-video-pane-shadow)' : 'none',
      })}
    >
      {isSelfViewCompact ? (
        <>
          <Box className={styles.selfViewCompactBar}>
            {renderSelfViewStatus(true)}
            {renderSelfViewToggleButton(true)}
          </Box>
          {showSelfViewNotice && <Box className={styles.selfViewNotice}>{t('selfViewHiddenNotice')}</Box>}
        </>
      ) : (
        <>
          <Box className={styles.infoContainer}>
            <Box className={classnames(styles.dotMenu, styles[`dotMenu-${dotMenuPosition}`])}>
              <div className={styles.nq}>
                <NQIcon nq={nq} />
              </div>

              <button
                type='button'
                onClick={togglePiP}
                className={classnames(styles.pipButton, {
                  [styles.pipButtonActive]: isPiP,
                })}
                aria-label={isPiP ? 'Exit Picture in Picture' : 'Picture in Picture'}
              >
                <PipIcon
                  sx={{
                    color: '#FFF',
                    opacity: isPiP ? 1 : 0.8,
                  }}
                />
              </button>

              {!isLocalParticipant && isHost && (
                <ParticipantDotMenu
                  withMute
                  withRemove
                  participant={participant as RemoteParticipant}
                  toggleClassName={styles.dotMenuToggle}
                  iconClassName={styles.dotMenuIcon}
                  iconColor={forceLightThemeForGuest ? '#393534' : '#FFF'}
                  menuBackgroundColor={forceLightThemeForGuest ? '#FFFFFF' : 'rgba(0, 0, 0, 0.5)'}
                  listItemTextColor={forceLightThemeForGuest ? '#393534' : '#FFF'}
                  tooltipPlacement={dotMenuPosition === 'bottom' ? 'right-end' : 'right-start'}
                />
              )}
            </Box>

            <Box className={styles.infoRowBottom}>
              {withIdentity &&
                (isSelfViewToggleEnabled ? (
                  renderSelfViewStatus()
                ) : (
                  <Box component='span' className={styles.identity}>
                    {selectedParticipant === participant && <PinIcon className={styles.star} />}
                    {(!isAudioEnabled || participantMuted) && <MicOffIcon className={styles.micOff} />}
                    {isVideoOff && <VideoCameraOffIcon className={styles.cameraOff} />}
                    {showStar && <StarIcon className={styles.star} />}
                    <Typography variant='body1' component='span' sx={{ color: '#FFF' }}>
                      {isLocalParticipant ? t('chatYou') : preferredFullName}
                    </Typography>
                  </Box>
                ))}
            </Box>
          </Box>

          {isSelfViewToggleEnabled && renderSelfViewToggleButton()}

          <Box className={styles.innerContainer}>
            {isVideoOff && <UserIcon sx={{ color: 'var(--app-text-muted)', fontSize: '8.8rem' }} />}
            {(isParticipantReconnecting || isReconnecting) && <ReconnectingAnimation />}
            {children}
          </Box>
        </>
      )}
    </Box>
  );
}
